import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { FilterBar } from '@/components/ui/FilterBar';
import { Icon } from '@/components/ui/Icon';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { ToolCard } from '@/components/ui/ToolCard';
import { ALL_FILTER } from '@/data/categories';
import { TOOLS, TOOL_CATEGORIES } from '@/data/tools';
import { useSearchShortcut } from '@/hooks/useSearchShortcut';
import { filterTools } from '@/lib/search';
import type { ToolCategory } from '@/types';
import styles from './Tools.module.css';

type CategoryFilter = typeof ALL_FILTER | ToolCategory;

const FILTER_OPTIONS: readonly CategoryFilter[] = [ALL_FILTER, ...TOOL_CATEGORIES];

/**
 * The categories holding the most tools, offered as a way out of a dead-end
 * search. Derived from the catalogue so it cannot drift as tools are added.
 */
const TOOLS_PER_CATEGORY = TOOLS.reduce<Partial<Record<ToolCategory, number>>>((counts, tool) => {
  counts[tool.category] = (counts[tool.category] ?? 0) + 1;
  return counts;
}, {});

const SUGGESTED_CATEGORIES: readonly ToolCategory[] = [...TOOL_CATEGORIES]
  .sort((a, b) => (TOOLS_PER_CATEGORY[b] ?? 0) - (TOOLS_PER_CATEGORY[a] ?? 0))
  .slice(0, 4);

export interface ToolsProps {
  readonly titleId: string;
}

export function Tools({ titleId }: ToolsProps) {
  const [category, setCategory] = useState<CategoryFilter>(ALL_FILTER);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // The entrance cascade belongs to arriving at the view. Left on, it replays
  // for every card that re-enters the filtered list, so widening a query — or
  // just holding Backspace — makes the whole grid pulse.
  const firstPaint = useRef(true);
  useEffect(() => {
    firstPaint.current = false;
  }, []);

  useSearchShortcut(searchRef);

  const visible = useMemo(
    () => filterTools(TOOLS, { category: category === ALL_FILTER ? null : category, query }),
    [category, query],
  );

  // Both controls below remove themselves as soon as they are used, which would
  // drop focus to <body>. Hand it back to the search box instead.
  const clearQuery = useCallback(() => {
    setQuery('');
    searchRef.current?.focus();
  }, []);

  const reset = useCallback(() => {
    setCategory(ALL_FILTER);
    setQuery('');
    searchRef.current?.focus();
  }, []);

  /**
   * Taking a suggestion dismisses the empty state that holds the button, so
   * focus has to be handed somewhere before it lands on <body>.
   */
  const takeSuggestion = useCallback((suggestion: ToolCategory) => {
    setQuery('');
    setCategory(suggestion);
    searchRef.current?.focus();
  }, []);

  return (
    <>
      <SectionHeader
        eyebrow="الأدوات"
        title="أدوات الذكاء الاصطناعي المجانية"
        titleId={titleId}
        lede={`عرض ${visible.length} من ${TOOLS.length} أداة — جميعها مجانية أو بها خطة مجانية.`}
      />

      <div className={styles.search}>
        <Icon name="search" size={20} className={styles.searchIcon} />
        <input
          ref={searchRef}
          type="search"
          className={styles.searchInput}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="ابحث باسم الأداة أو التصنيف…"
          aria-label="ابحث في الأدوات"
        />
        {query ? (
          <Button variant="subtle" onClick={clearQuery}>
            مسح
          </Button>
        ) : (
          // Hidden from assistive tech and from touch devices: it is a hint for
          // pointer-and-keyboard visitors, not a control.
          <kbd className={styles.shortcut} aria-hidden="true">
            /
          </kbd>
        )}
      </div>

      <FilterBar
        options={FILTER_OPTIONS}
        value={category}
        onChange={setCategory}
        label="تصفية حسب التصنيف"
      />

      {/* Announce result-count changes without moving focus away from the input. */}
      <p className="visually-hidden" role="status" aria-live="polite">
        {`عرض ${visible.length} من ${TOOLS.length} أداة`}
      </p>

      {visible.length > 0 ? (
        <div className={styles.grid}>
          {visible.map((tool, index) => (
            <ToolCard key={tool.name} tool={tool} index={index} animate={firstPaint.current} />
          ))}
        </div>
      ) : (
        <EmptyState
          title="لا توجد نتائج"
          body={`لا توجد أداة تطابق «${query.trim()}». جرّب أحد التصنيفات التالية:`}
          action={
            <div className={styles.suggestions}>
              {/* A dead end is the worst outcome for a directory, so offer the
                  busiest categories as a way back into the catalogue. */}
              <div className={styles.suggestionRow} role="group" aria-label="تصنيفات مقترحة">
                {SUGGESTED_CATEGORIES.map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="quiet"
                    onClick={() => takeSuggestion(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>

              <Button variant="subtle" onClick={reset}>
                إعادة التعيين
              </Button>
            </div>
          }
        />
      )}
    </>
  );
}
